import { useState, useEffect, useCallback, useRef } from 'react';
import { LOD_LEVELS, getAutoUpgradeLOD, stfPointToCapturePoint } from '../lib/heatmapUtils';

/**
 * Hook managing all data loading for an area section:
 * - GCS progressive file loading with caching
 * - BigQuery LOD loading with background progressive upgrade
 * - STF scene scan data loading
 * - Map config loading and retry
 * - Filter/date change tracking and cache invalidation
 */
function useAreaData({ api, area, daysFilter, startDate, endDate, filters, dataSource, expanded }) {
  // Core data state
  const [loaded, setLoaded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [mapConfig, setMapConfig] = useState(null);
  const [fileLimit, setFileLimit] = useState(50);

  // LOD state for BigQuery data source
  const [currentLOD, setCurrentLOD] = useState(0);
  const [, setLodData] = useState(null);
  const lodCacheRef = useRef(new Map());
  const lodLoadingRef = useRef(false);
  const zoneBoundsRef = useRef(null);
  const progressiveLoadingRef = useRef(null);
  const [lodProgress, setLodProgress] = useState({});

  // GCS progressive loading refs
  const loadInProgressRef = useRef(false);
  const fileDataCacheRef = useRef(new Map());

  // Change tracking refs
  const prevDaysFilterRef = useRef(daysFilter);
  const prevFiltersRef = useRef(JSON.stringify(filters));
  const prevFileLimitRef = useRef(fileLimit);
  const prevDataSourceRef = useRef(dataSource);
  const prevDateKeyRef = useRef(`${startDate}-${endDate}`);

  // STF layer state
  const [showAWSLayer, setShowAWSLayer] = useState(!area._stfOnly);
  const [showSTFLayer, setShowSTFLayer] = useState(!!area.stfZone);
  const [stfScanPoints, setStfScanPoints] = useState(null);
  const [stfLoading, setStfLoading] = useState(false);
  const [stfSelectedScans, setStfSelectedScans] = useState(new Set()); // Set of test_run_ids
  const [stfScanHistory, setStfScanHistory] = useState(null);
  const [stfReports, setStfReports] = useState(null);
  const stfPointsCacheRef = useRef(new Map()); // sorted testRunId key -> aggregated points

  // Device runs metadata (BigQuery mode)
  const [deviceRuns, setDeviceRuns] = useState(null);

  // Start progressive loading of LOD 1 and LOD 2 in background after LOD 0 loads
  const startProgressiveLoading = useCallback(async (areaName, days, currentFilters, runLimit, sd = null, ed = null) => {
    if (progressiveLoadingRef.current) {
      progressiveLoadingRef.current.abort();
    }

    const controller = new AbortController();
    progressiveLoadingRef.current = controller;
    const signal = controller.signal;

    setLodProgress({ 0: 'loaded', 1: 'pending', 2: 'pending' });

    // Fetch LOD 1 (2m cells)
    if (!signal.aborted) {
      setLodProgress(prev => ({ ...prev, 1: 'loading' }));
      try {
        console.log(`[useAreaData] Background loading LOD 1 (2m cells) with runLimit=${runLimit}...`);
        const lod1Result = await api.getBigQueryHeatmapDataLOD(areaName, days, currentFilters, {
          cellSize: LOD_LEVELS[1].cellSize,
          runLimit
        }, sd, ed);
        if (!signal.aborted) {
          lodCacheRef.current.set(1, lod1Result);
          setLodProgress(prev => ({ ...prev, 1: 'loaded' }));
          console.log('[useAreaData] Background loaded LOD 1:', { cellCount: lod1Result.cellCount });
        }
      } catch (err) {
        if (!signal.aborted) {
          setLodProgress(prev => ({ ...prev, 1: 'error' }));
          console.warn('[useAreaData] Background LOD 1 failed:', err.message);
        }
      }
    }

    // Fetch LOD 2 (full resolution)
    if (!signal.aborted) {
      setLodProgress(prev => ({ ...prev, 2: 'loading' }));
      try {
        const lodOptions = {
          cellSize: null,
          runLimit
        };
        console.log(`[useAreaData] Background loading LOD 2 (full resolution) with runLimit=${runLimit}...`);
        const lod2Result = await api.getBigQueryHeatmapDataLOD(areaName, days, currentFilters, lodOptions, sd, ed);
        if (!signal.aborted) {
          lodCacheRef.current.set(2, lod2Result);
          setLodProgress(prev => ({ ...prev, 2: 'loaded' }));
          console.log('[useAreaData] Background loaded LOD 2:', { pointCount: lod2Result.totalPoints || lod2Result.capturePoints?.length });
        }
      } catch (err) {
        if (!signal.aborted) {
          setLodProgress(prev => ({ ...prev, 2: 'error' }));
          console.warn('[useAreaData] Background LOD 2 failed:', err.message);
        }
      }
    }
  }, [api]);

  // Retry loading a specific LOD level that failed
  // eslint-disable-next-line no-unused-vars
  const retryLODLevel = useCallback(async (level) => {
    if (lodProgress[level] !== 'error') return;

    setLodProgress(prev => ({ ...prev, [level]: 'loading' }));

    try {
      const lodConfig = LOD_LEVELS[level];
      const lodOptions = {
        cellSize: lodConfig.cellSize,
        runLimit: fileLimit
      };

      console.log(`[useAreaData] Retrying LOD ${level} with runLimit=${fileLimit}...`, lodOptions);
      const result = await api.getBigQueryHeatmapDataLOD(area.name, daysFilter, filters, lodOptions, startDate, endDate);

      lodCacheRef.current.set(level, result);
      setLodProgress(prev => ({ ...prev, [level]: 'loaded' }));
      console.log(`[useAreaData] Retry LOD ${level} succeeded:`, {
        cellCount: result.cellCount,
        pointCount: result.totalPoints || result.capturePoints?.length
      });
    } catch (err) {
      setLodProgress(prev => ({ ...prev, [level]: 'error' }));
      console.warn(`[useAreaData] Retry LOD ${level} failed:`, err.message);
    }
  }, [api, area.name, daysFilter, startDate, endDate, filters, fileLimit, lodProgress]);

  // Main data loading function
  const loadData = useCallback(async () => {
    if (loadInProgressRef.current) return;
    loadInProgressRef.current = true;

    setLoading(true);
    setError(null);

    try {
      // BigQuery data source - start with LOD 0 (overview) for fast initial load
      if (dataSource === 'bigquery') {
        lodCacheRef.current.clear();
        setCurrentLOD(0);
        setLodData(null);

        const mapConfigName = area.zoneName || area.name;
        console.log('[useAreaData] BigQuery LOD loading: starting with LOD 0 for', mapConfigName);

        const [lodResult, mapResult, runsResult] = await Promise.all([
          api.getBigQueryHeatmapDataLOD(area.name, daysFilter, filters, {
            cellSize: LOD_LEVELS[0].cellSize,
            runLimit: fileLimit
          }, startDate, endDate),
          api.getHeatmapMapConfig(mapConfigName).catch((err) => {
            console.log('[useAreaData] Map config error:', err.message);
            return null;
          }),
          api.getBigQueryHeatmapRuns(area.name, daysFilter, filters, startDate, endDate).catch((err) => {
            console.warn('[useAreaData] Runs metadata error:', err.message);
            return { runs: [] };
          })
        ]);

        setDeviceRuns(runsResult.runs || []);

        console.log('[useAreaData] LOD 0 result:', {
          cellCount: lodResult.cellCount,
          totalSamples: lodResult.totalSamples,
          queryTimeMs: lodResult.queryTimeMs
        });

        if (lodResult.bounds) {
          zoneBoundsRef.current = lodResult.bounds;
        }

        lodCacheRef.current.set(0, lodResult);
        setLodData(lodResult);

        // Transform map config
        console.log('[useAreaData] Map config result:', { mapConfigName, hasResult: !!mapResult, bounds: mapResult?.bounds, imageUrl: mapResult?.imageUrl });
        if (mapResult?.bounds && mapResult?.imageUrl) {
          let finalImageUrl = mapResult.imageUrl;
          if (mapResult.imageUrl.startsWith('/')) {
            try {
              console.log('[useAreaData] Fetching map image blob for', mapConfigName);
              finalImageUrl = await api.getHeatmapMapImage(mapConfigName);
              console.log('[useAreaData] Map image blob URL:', finalImageUrl?.substring(0, 50));
            } catch (imgErr) {
              console.warn('[useAreaData] Failed to fetch map image:', imgErr.message);
              finalImageUrl = null;
            }
          }
          if (finalImageUrl) {
            console.log('[useAreaData] Setting mapConfig for', mapConfigName);
            setMapConfig({
              imageUrl: finalImageUrl,
              minX: mapResult.bounds.minX,
              maxX: mapResult.bounds.maxX,
              minZ: mapResult.bounds.minZ,
              maxZ: mapResult.bounds.maxZ
            });
          } else {
            console.warn('[useAreaData] No final image URL for', mapConfigName);
          }
        } else {
          console.log('[useAreaData] Skipping map config — missing bounds or imageUrl');
        }

        // Convert LOD cells to capturePoints format for rendering
        const capturePoints = (lodResult.cells || []).map(cell => ({
          position: cell.position,
          rotation: cell.rotation,
          framerate: cell.fps?.min,
          gpuTime: cell.gpuTime?.avg ? 1000 / (1000 / cell.gpuTime.avg) : null,
          cpuTime: cell.cpuTime?.avg ? 1000 / (1000 / cell.cpuTime.avg) : null,
          drawCalls: cell.drawCalls?.avg,
          batches: cell.batches?.avg,
          triangles: cell.triangles?.avg,
          vertices: cell.vertices?.avg,
          _isLODCell: true,
          _cellSize: lodResult.cellSize,
          _sampleCount: cell.sampleCount,
          _deviceCount: cell.deviceCount,
          _devices: cell.devices,
          _fpsStats: cell.fps,
          _source: `LOD${lodResult.lodLevel}_${cell.gridX}_${cell.gridZ}`,
          _device: cell.devices?.join(', ') || 'Multiple',
          _quality: 'Aggregated'
        }));

        setData({
          files: [],
          filesLoaded: 0,
          totalFiles: 0,
          totalPoints: capturePoints.length,
          capturePoints,
          _isLOD: true,
          _lodLevel: lodResult.lodLevel,
          _cellSize: lodResult.cellSize,
          _totalSamples: lodResult.totalSamples
        });
        setLoaded(true);
        setLoading(false);
        loadInProgressRef.current = false;

        startProgressiveLoading(area.name, daysFilter, filters, fileLimit, startDate, endDate);
        return;
      }

      // GCS data source - progressive loading with caching
      const [metadataResult, mapResult] = await Promise.all([
        api.getHeatmapAreaData(area.name, fileLimit, daysFilter, filters, true, startDate, endDate),
        api.getHeatmapMapConfig(area.name).catch(() => null)
      ]);

      const cache = fileDataCacheRef.current;
      const requestedFiles = metadataResult.files || [];

      const cachedFileNames = new Set();
      const uncachedFileNames = [];
      for (const file of requestedFiles) {
        if (cache.has(file.name)) {
          cachedFileNames.add(file.name);
        } else {
          uncachedFileNames.push(file.name);
        }
      }

      const filesWithData = requestedFiles.map(metaFile => {
        const cached = cache.get(metaFile.name);
        if (cached) {
          return { ...cached, loading: false };
        }
        return { ...metaFile, loading: true };
      });

      let allCapturePoints = [];
      for (const file of requestedFiles) {
        const cached = cache.get(file.name);
        if (cached?.capturePoints) {
          allCapturePoints = allCapturePoints.concat(cached.capturePoints);
        }
      }

      setData({
        ...metadataResult,
        files: filesWithData,
        filesLoaded: cachedFileNames.size,
        totalPoints: allCapturePoints.length,
        capturePoints: allCapturePoints
      });
      setLoading(false);

      // Transform map config
      if (mapResult?.bounds && mapResult?.imageUrl) {
        let finalImageUrl = mapResult.imageUrl;
        if (mapResult.imageUrl.startsWith('/')) {
          try {
            finalImageUrl = await api.getHeatmapMapImage(area.name);
          } catch (imgErr) {
            console.warn('[useAreaData] Failed to fetch map image:', imgErr.message);
            finalImageUrl = null;
          }
        }
        if (finalImageUrl) {
          setMapConfig({
            imageUrl: finalImageUrl,
            minX: mapResult.bounds.minX,
            maxX: mapResult.bounds.maxX,
            minZ: mapResult.bounds.minZ,
            maxZ: mapResult.bounds.maxZ
          });
        }
      }

      // Progressive loading of uncached files
      if (uncachedFileNames.length > 0 && loadInProgressRef.current) {
        const totalNeeded = requestedFiles.length;
        const alreadyCached = cachedFileNames.size;
        const loadStages = [2, 5, 10, 20, 50].filter(n => n <= totalNeeded && n > alreadyCached);
        loadStages.push(totalNeeded);

        for (const limit of loadStages) {
          if (!loadInProgressRef.current) break;
          try {
            const fullResult = await api.getHeatmapAreaData(area.name, limit, daysFilter, filters, false, startDate, endDate);
            if (!loadInProgressRef.current) break;

            const allPoints = fullResult.capturePoints || [];
            for (const file of fullResult.files) {
              if (!cache.has(file.name)) {
                const filePoints = allPoints.filter(p => p._source === file.name);
                cache.set(file.name, { ...file, capturePoints: filePoints });
              }
            }

            const updatedFiles = requestedFiles.map(metaFile => {
              const cached = cache.get(metaFile.name);
              if (cached) {
                return { ...cached, loading: false };
              }
              return { ...metaFile, loading: true };
            });
            const loadedCount = updatedFiles.filter(f => !f.loading).length;

            let points = [];
            for (const file of requestedFiles) {
              const cached = cache.get(file.name);
              if (cached?.capturePoints) {
                points = points.concat(cached.capturePoints);
              }
            }

            setData({
              ...fullResult,
              files: updatedFiles,
              filesLoaded: loadedCount,
              totalFiles: requestedFiles.length,
              totalPoints: points.length,
              capturePoints: points
            });
          } catch (err) {
            console.warn('[useAreaData] Failed to load more files:', err.message);
            break;
          }
        }
      }

      setLoaded(true);
    } catch (err) {
      setError(err.message);
      setLoading(false);
    } finally {
      loadInProgressRef.current = false;
    }
  }, [api, area.name, daysFilter, startDate, endDate, filters, fileLimit, dataSource, startProgressiveLoading, area.zoneName]);

  // LOD change handler - called by HeatmapViewer when zoom level changes
  const handleLODChange = useCallback(async (newLODLevel, viewport) => {
    if (dataSource !== 'bigquery') return;

    const currentPoints = data?.totalPoints || 0;
    const upgrade = getAutoUpgradeLOD(newLODLevel, currentPoints, lodCacheRef);
    const targetLOD = upgrade ? upgrade.level : newLODLevel;

    if (targetLOD === currentLOD) return;

    const lodConfig = LOD_LEVELS[targetLOD];
    if (!lodConfig) return;

    console.log(`[useAreaData] LOD change: ${currentLOD} -> ${targetLOD} (${lodConfig.label}, reason: ${upgrade ? 'budget' : 'zoom'})`);

    // Check cache first
    if (lodCacheRef.current.has(targetLOD)) {
      console.log(`[useAreaData] Using cached LOD ${targetLOD} data`);
      const cachedData = lodCacheRef.current.get(targetLOD);
      setCurrentLOD(targetLOD);
      setLodData(cachedData);

      const capturePoints = (cachedData.cells || cachedData.capturePoints || []).map(cell => {
        if (cell._isLODCell !== undefined) return cell;
        return {
          position: cell.position,
          rotation: cell.rotation,
          framerate: cell.fps?.min ?? cell.framerate,
          gpuTime: cell.gpuTime?.avg ?? cell.gpuTime,
          cpuTime: cell.cpuTime?.avg ?? cell.cpuTime,
          drawCalls: cell.drawCalls?.avg ?? cell.drawCalls,
          batches: cell.batches?.avg ?? cell.batches,
          triangles: cell.triangles?.avg ?? cell.triangles,
          vertices: cell.vertices?.avg ?? cell.vertices,
          _isLODCell: cachedData.cellSize != null,
          _cellSize: cachedData.cellSize,
          _sampleCount: cell.sampleCount || 1,
          _deviceCount: cell.deviceCount || 1,
          _devices: cell.devices || [],
          _fpsStats: cell.fps || { min: cell.framerate, max: cell.framerate, avg: cell.framerate },
          _source: cell._source || `LOD${targetLOD}_${cell.gridX || 0}_${cell.gridZ || 0}`,
          _device: cell._device || cell.devices?.join(', ') || 'Unknown',
          _quality: cell._quality || 'Aggregated'
        };
      });

      setData(prev => ({
        ...prev,
        capturePoints,
        totalPoints: capturePoints.length,
        _isLOD: lodConfig.cellSize != null,
        _lodLevel: targetLOD,
        _cellSize: lodConfig.cellSize,
        _totalSamples: cachedData.totalSamples || capturePoints.length
      }));
      return;
    }

    // Need to fetch new LOD data
    if (lodLoadingRef.current) return;
    lodLoadingRef.current = true;
    setCurrentLOD(targetLOD);

    try {
      const lodOptions = {
        cellSize: lodConfig.cellSize,
        runLimit: fileLimit
      };

      console.log(`[useAreaData] Fetching LOD ${targetLOD} with runLimit=${fileLimit}...`, lodOptions);
      const lodResult = await api.getBigQueryHeatmapDataLOD(area.name, daysFilter, filters, lodOptions, startDate, endDate);

      console.log(`[useAreaData] LOD ${targetLOD} result:`, {
        cellCount: lodResult.cellCount,
        pointCount: lodResult.totalPoints,
        totalSamples: lodResult.totalSamples,
        queryTimeMs: lodResult.queryTimeMs
      });

      lodCacheRef.current.set(targetLOD, lodResult);
      setLodData(lodResult);

      const sourceData = lodResult.cells || lodResult.capturePoints || [];
      const capturePoints = sourceData.map(cell => ({
        position: cell.position,
        rotation: cell.rotation,
        framerate: cell.fps?.min ?? cell.framerate,
        gpuTime: cell.gpuTime?.avg ?? cell.gpuTime,
        cpuTime: cell.cpuTime?.avg ?? cell.cpuTime,
        drawCalls: cell.drawCalls?.avg ?? cell.drawCalls,
        batches: cell.batches?.avg ?? cell.batches,
        triangles: cell.triangles?.avg ?? cell.triangles,
        vertices: cell.vertices?.avg ?? cell.vertices,
        _isLODCell: lodConfig.cellSize != null,
        _cellSize: lodConfig.cellSize,
        _sampleCount: cell.sampleCount || 1,
        _deviceCount: cell.deviceCount || 1,
        _devices: cell.devices || [],
        _fpsStats: cell.fps || { min: cell.framerate, max: cell.framerate, avg: cell.framerate },
        _source: cell._source || `LOD${targetLOD}_${cell.gridX || 0}_${cell.gridZ || 0}`,
        _device: cell._device || cell.devices?.join(', ') || 'Unknown',
        _quality: cell._quality || 'Aggregated'
      }));

      setData(prev => ({
        ...prev,
        capturePoints,
        totalPoints: capturePoints.length,
        _isLOD: lodConfig.cellSize != null,
        _lodLevel: targetLOD,
        _cellSize: lodConfig.cellSize,
        _totalSamples: lodResult.totalSamples || capturePoints.length
      }));
    } catch (err) {
      console.error(`[useAreaData] Failed to fetch LOD ${targetLOD}:`, err.message);
      setCurrentLOD(currentLOD);
    } finally {
      lodLoadingRef.current = false;
    }
  }, [dataSource, currentLOD, data, api, area.name, daysFilter, startDate, endDate, filters, fileLimit]);

  // Reset loaded state when daysFilter, filters, or dataSource change (clear cache too)
  useEffect(() => {
    const filtersStr = JSON.stringify(filters);
    const dateKey = `${startDate}-${endDate}`;
    if (prevDaysFilterRef.current !== daysFilter || prevFiltersRef.current !== filtersStr || prevDataSourceRef.current !== dataSource || prevDateKeyRef.current !== dateKey) {
      prevDaysFilterRef.current = daysFilter;
      prevFiltersRef.current = filtersStr;
      prevDataSourceRef.current = dataSource;
      prevDateKeyRef.current = dateKey;
      fileDataCacheRef.current.clear();

      if (progressiveLoadingRef.current) {
        progressiveLoadingRef.current.abort();
        progressiveLoadingRef.current = null;
      }
      setLodProgress({});

      setLoaded(false);
      setData(null);
    }
  }, [daysFilter, startDate, endDate, filters, dataSource]);

  // When fileLimit changes, clear LOD cache and trigger reload
  useEffect(() => {
    if (prevFileLimitRef.current !== fileLimit) {
      prevFileLimitRef.current = fileLimit;
      if (expanded) {
        if (dataSource === 'bigquery') {
          lodCacheRef.current.clear();
          setLodProgress({});
        }
        setLoaded(false);
      }
    }
  }, [fileLimit, expanded, dataSource]);

  // Reload data when filter changes and section is expanded
  useEffect(() => {
    if (expanded && !loaded && !loading) {
      if (area._stfOnly) {
        setLoaded(true);
      } else {
        loadData();
      }
    }
  }, [expanded, loaded, loading, loadData, area._stfOnly]);

  // Load STF scan history + most recent scan points when expanded with STF data
  // Respects the global date filter (daysFilter / startDate / endDate)
  useEffect(() => {
    if (!expanded || !area.stfZone || !showSTFLayer) return;
    let cancelled = false;
    async function loadSTF() {
      try {
        const stfZoneName = area.stfZone.zone_name;
        const mapConfigName = area.zoneName || area.name;
        const [historyResult, mapResult, reportsResult] = await Promise.all([
          api.getSTFScanHistory(stfZoneName, daysFilter, startDate, endDate),
          !mapConfig ? api.getHeatmapMapConfig(mapConfigName).catch(() => null) : Promise.resolve(null),
          api.getSTFScanReports(stfZoneName).catch(() => ({ reports: [] })),
        ]);
        if (cancelled) return;

        if (!mapConfig && mapResult?.bounds && mapResult?.imageUrl) {
          let imageUrl = mapResult.imageUrl;
          if (imageUrl.startsWith('/')) {
            try { imageUrl = await api.getHeatmapMapImage(mapConfigName); } catch { imageUrl = null; }
          }
          if (!cancelled && imageUrl) {
            setMapConfig({ imageUrl, minX: mapResult.bounds.minX, maxX: mapResult.bounds.maxX, minZ: mapResult.bounds.minZ, maxZ: mapResult.bounds.maxZ });
          }
        }

        const scans = historyResult.scans || [];
        setStfScanHistory(scans);
        if (!cancelled) setStfReports(reportsResult.reports || []);
        if (scans.length > 0) {
          // Filter scans by branch/CL if those filters are active
          let filtered = scans;
          if (filters.branches?.length) {
            filtered = filtered.filter(s =>
              filters.branches.some(b => s.client_branch && s.client_branch.includes(b))
            );
          }
          if (filters.cls?.length) {
            filtered = filtered.filter(s =>
              filters.cls.includes(String(s.client_changelist))
            );
          }
          // Select only the most recent scan by default to avoid loading 100K+ points
          // (history is sorted most-recent-first)
          if (filtered.length > 0) {
            setStfSelectedScans(new Set([filtered[0].test_run_id]));
          }
        }
      } catch (err) {
        console.warn('[useAreaData] STF scan load failed:', err.message);
      }
    }
    loadSTF();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expanded, area.stfZone, showSTFLayer, api, area.zoneName, area.name, mapConfig, daysFilter, startDate, endDate,
      // Serialize filters to avoid re-running on every render (object identity changes)
      // eslint-disable-next-line react-hooks/exhaustive-deps
      JSON.stringify(filters?.branches), JSON.stringify(filters?.cls)]);

  // Load STF points when selected scans change.
  // Single scan: fetches raw points (preserves exact values).
  // Multiple scans: uses server-side aggregation (averages per position+yaw)
  // so point count stays ~constant regardless of how many scans are selected.
  useEffect(() => {
    if (stfSelectedScans.size === 0 || !area.stfZone) {
      setStfScanPoints(null);
      setStfLoading(false);
      return;
    }
    let cancelled = false;

    async function loadPoints() {
      const zoneName = area.stfZone.zone_name;
      const selectedIds = [...stfSelectedScans];

      // Build a cache key from sorted IDs so we don't re-fetch identical selections
      const cacheKey = selectedIds.sort().join(',');
      const cache = stfPointsCacheRef.current;
      if (cache.has(cacheKey)) {
        setStfScanPoints(cache.get(cacheKey));
        setStfLoading(false);
        return;
      }

      setStfLoading(true);
      try {
        const r = await api.getSTFScanPointsAggregated(zoneName, selectedIds);
        if (cancelled) return;
        const points = (r.points || []).map(stfPointToCapturePoint);
        cache.set(cacheKey, points);
        setStfScanPoints(points);
      } catch (err) {
        console.warn('[useAreaData] STF aggregate load failed:', err.message);
        if (!cancelled) setStfScanPoints(null);
      }
      if (!cancelled) setStfLoading(false);
    }
    loadPoints();
    return () => { cancelled = true; };
  }, [stfSelectedScans, area.stfZone, api]);

  // Retry map config fetch if data loaded but map config is missing
  useEffect(() => {
    if (!expanded || !loaded || mapConfig || loading) return;

    let cancelled = false;
    async function retryMapConfig() {
      const mapConfigName = area.zoneName || area.name;
      try {
        const mapResult = await api.getHeatmapMapConfig(mapConfigName);
        if (cancelled || !mapResult?.bounds || !mapResult?.imageUrl) return;

        let imageUrl = mapResult.imageUrl;
        if (imageUrl.startsWith('/')) {
          try {
            imageUrl = await api.getHeatmapMapImage(mapConfigName);
          } catch { imageUrl = null; }
        }
        if (!cancelled && imageUrl) {
          setMapConfig({
            imageUrl,
            minX: mapResult.bounds.minX,
            maxX: mapResult.bounds.maxX,
            minZ: mapResult.bounds.minZ,
            maxZ: mapResult.bounds.maxZ
          });
        }
      } catch {
        // Silently ignore — map is optional
      }
    }
    retryMapConfig();
    return () => { cancelled = true; };
  }, [expanded, loaded, loading, mapConfig, api, area.zoneName, area.name]);

  // Auto-switch to better LOD when one becomes available in cache
  useEffect(() => {
    if (dataSource !== 'bigquery' || !data) return;

    const currentPoints = data.totalPoints || 0;
    const upgrade = getAutoUpgradeLOD(currentLOD, currentPoints, lodCacheRef);

    console.log(`[useAreaData] Auto-upgrade check: currentLOD=${currentLOD}, points=${currentPoints}, upgrade=`, upgrade, 'lodProgress=', lodProgress);

    if (upgrade) {
      if (lodProgress[upgrade.level] === 'loaded') {
        console.log(`[useAreaData] Auto-upgrading LOD ${currentLOD} -> ${upgrade.level} (${currentPoints} -> ${upgrade.points} points)`);
        handleLODChange(upgrade.level, null);
      } else {
        console.log(`[useAreaData] Upgrade available but LOD ${upgrade.level} not loaded yet (status: ${lodProgress[upgrade.level]})`);
      }
    }
  }, [lodProgress, dataSource, data, currentLOD, handleLODChange]);

  return {
    // Core state
    loaded, loading, error, data, mapConfig,
    fileLimit, setFileLimit,
    loadData,
    // LOD
    currentLOD, lodProgress, lodCacheRef,
    handleLODChange,
    // Device runs (BigQuery)
    deviceRuns,
    // STF
    showAWSLayer, setShowAWSLayer,
    showSTFLayer, setShowSTFLayer,
    stfScanPoints, stfLoading,
    stfSelectedScans, setStfSelectedScans,
    stfScanHistory,
    stfReports,
  };
}

export default useAreaData;
